import { authenticatedHttpClient } from './httpClient';

export const fetchAvailablePaymentMethods = async (
  organizationId: string
): Promise<Array<'stripe' | 'ves'>> => {
  const res = await authenticatedHttpClient.get<{ enabled?: string[] }>(
    `/payments/available-methods?organizationId=${encodeURIComponent(organizationId)}`
  );
  const enabled = Array.isArray(res?.enabled) ? res.enabled : [];
  const filtered = enabled.filter(
    (m): m is 'stripe' | 'ves' => m === 'stripe' || m === 'ves'
  );
  return filtered.length > 0 ? filtered : ['stripe', 'ves'];
};

export const createCheckoutSession = async (params: {
  organizationId: string;
  plan: string;
  successUrl: string;
  cancelUrl: string;
}): Promise<{ id: string }> => {
  const res = await authenticatedHttpClient.post(
    `/payments/checkout-session`,
    params
  );
  return res as { id: string };
};

export const createBillingPortalSession = async (params: {
  organizationId: string;
  customerId: string;
  returnUrl: string;
}): Promise<{ url: string }> => {
  const res = await authenticatedHttpClient.post(
    `/payments/billing-portal`,
    params
  );
  return res as { url: string };
};

/** Submit a VES payment request (server creates Firestore record + pending_ves). */
export const submitVesPaymentRequest = async (params: {
  organizationId: string;
  plan: string;
  exchangeRate: number;
  exchangeSourceId?: string;
  exchangeSourceName?: string;
  captureUrl: string;
  storagePath: string;
  amountUSD: number;
  amountVES: number;
}): Promise<{ requestId: string }> => {
  const res = await authenticatedHttpClient.post(`/payments/ves-submit`, params);
  return res as { requestId: string };
};

/** Create Binance Pay order for USDT subscription payment */
export const createBinancePayOrder = async (params: {
  organizationId: string;
  organizationName?: string;
  plan: string;
  successUrl?: string;
  cancelUrl?: string;
}): Promise<{
  checkoutUrl: string;
  qrcodeLink: string;
  universalUrl?: string;
  totalFee: string;
  currency: string;
}> => {
  const res = await authenticatedHttpClient.post(
    `/payments/binance/create-order`,
    params
  );
  return res as any;
};

/** Start a 30-day free trial for an organization (server-side; Standard plan). */
export const startFreeTrial = async (params: {
  organizationId: string;
}): Promise<{
  organizationId: string;
  trialEndsAt: string;
}> => {
  const res = await authenticatedHttpClient.post(`/payments/start-trial`, params);
  return res as { organizationId: string; trialEndsAt: string };
};

export const createIntelligenceCheckout = async (params: {
  organizationId: string;
  capacityId: 'lite' | 'plus' | 'extra';
  successUrl: string;
  cancelUrl: string;
  paymentMethod?: 'stripe' | 'ves' | 'binance';
  exchangeRate?: number;
  exchangeSourceId?: string;
  exchangeSourceName?: string;
  captureUrl?: string;
  storagePath?: string;
  amountUSD?: number;
  amountVES?: number;
}): Promise<{
  mode: string;
  url?: string | null;
  capacityId?: string;
  capacityName?: string;
  priceId?: string;
  price?: number;
  currency?: string;
  monthlyCredits?: number;
  paymentMethod?: string;
  requestId?: string;
  checkoutUrl?: string;
  qrcodeLink?: string;
  universalUrl?: string;
}> => {
  return authenticatedHttpClient.post(`/payments/intelligence/checkout`, params) as Promise<{
    mode: string;
    url?: string | null;
    capacityId?: string;
    capacityName?: string;
    priceId?: string;
    price?: number;
    currency?: string;
    monthlyCredits?: number;
    paymentMethod?: string;
    requestId?: string;
    checkoutUrl?: string;
    qrcodeLink?: string;
    universalUrl?: string;
  }>;
};

export const buyIntelligenceCredits = async (params: {
  organizationId: string;
  packId: string;
  quantity: number;
  paymentMethod: 'stripe' | 'ves' | 'binance';
  successUrl: string;
  cancelUrl: string;
  exchangeRate?: number;
  exchangeSourceId?: string;
  exchangeSourceName?: string;
  captureUrl?: string;
  storagePath?: string;
  amountUSD?: number;
  amountVES?: number;
}): Promise<{
  mode: string;
  url?: string | null;
  credits?: number;
  quantity?: number;
  packId?: string;
  paymentMethod?: string;
  requestId?: string;
  checkoutUrl?: string;
  qrcodeLink?: string;
  amountUSD?: number;
}> => {
  return authenticatedHttpClient.post(
    `/payments/intelligence/buy-credits`,
    params
  ) as Promise<{
    mode: string;
    url?: string | null;
    credits?: number;
    quantity?: number;
    packId?: string;
    paymentMethod?: string;
    requestId?: string;
    checkoutUrl?: string;
    qrcodeLink?: string;
    amountUSD?: number;
  }>;
};

export const cancelIntelligence = async (params: {
  organizationId: string;
}): Promise<{ canceled: boolean }> => {
  return authenticatedHttpClient.post(`/payments/intelligence/cancel`, params) as Promise<{
    canceled: boolean;
  }>;
};

export const startIntelligenceTrial = async (params: {
  organizationId: string;
  capacityId?: 'lite' | 'plus' | 'extra';
  trialDays?: number;
}): Promise<{
  organizationId: string;
  capacityId?: string | null;
  trialing?: boolean;
  periodEnd?: string | null;
  creditsAllowance?: number;
}> => {
  return authenticatedHttpClient.post(
    `/payments/intelligence/start-trial`,
    params
  ) as Promise<{
    organizationId: string;
    capacityId?: string | null;
    trialing?: boolean;
    periodEnd?: string | null;
    creditsAllowance?: number;
  }>;
};

/** Notify platform admin when a user starts the free trial (sends email) */
export const notifyTrialStarted = async (params: {
  organizationId: string;
  organizationName: string;
  trialEndsAt: string;
}): Promise<void> => {
  await authenticatedHttpClient.post(`/payments/trial-notify`, params);
};

/** Notify platform admin about a new VES payment request (sends email) */
export const notifyVesPaymentPending = async (params: {
  organizationId: string;
  organizationName: string;
  plan: string;
  amountUSD: number;
  amountVES: number;
  exchangeRate: number;
  exchangeSourceName?: string;
  captureUrl: string;
  requestId: string;
}): Promise<void> => {
  await authenticatedHttpClient.post(`/payments/ves-notify-pending`, params);
};
